/**
 * Sync Queue Manager — flushes pending offline writes to Supabase when connectivity returns.
 *
 * Designed to be called from the useOfflineReady hook on the `online` event.
 * Each queued entry is a prepareSavePayload() result for a single appraisal.
 */
import { supabase } from '@/integrations/supabase/client';
import {
  getPendingSyncEntries,
  removeSyncEntry,
  enqueueSyncWrite,
  getPendingPhotos,
  removePendingPhoto,
  type SyncQueueEntry,
  type PendingPhoto,
} from '@/lib/offlineStore';
import { getAppraisalStoragePath } from '@/utils/storagePaths';

export type SyncStatus = 'idle' | 'syncing' | 'done' | 'error';

export interface SyncResult {
  flushed: number;
  failed: number;
  conflicts: SyncConflict[];
}

export interface SyncConflict {
  appraisalId: string;
  /** Server's updated_at was newer than our offline write */
  serverUpdatedAt: string;
  localCreatedAt: string;
}

/**
 * Flush all pending sync entries for a user.
 *
 * Strategy: last-write-wins with conflict detection.
 * If the server's `updated_at` is newer than the queued entry's `createdAt`,
 * we still apply the write (appraiser in the field is the primary editor)
 * but record it as a conflict for the UI to surface if desired.
 */
export async function flushSyncQueue(
  userId: string,
  onProgress?: (status: SyncStatus) => void,
): Promise<SyncResult> {
  const entries = await getPendingSyncEntries(userId);
  if (entries.length === 0) {
    onProgress?.('done');
    return { flushed: 0, failed: 0, conflicts: [] };
  }

  onProgress?.('syncing');
  let flushed = 0;
  let failed = 0;
  const conflicts: SyncConflict[] = [];

  for (const entry of entries) {
    try {
      // Check for conflicts: fetch server's updated_at
      const { data: serverRow } = await supabase
        .from('appraisals')
        .select('updated_at')
        .eq('id', entry.appraisalId)
        .single();

      if (serverRow) {
        const serverTime = new Date(serverRow.updated_at).getTime();
        const localTime = new Date(entry.createdAt).getTime();
        if (serverTime > localTime) {
          conflicts.push({
            appraisalId: entry.appraisalId,
            serverUpdatedAt: serverRow.updated_at,
            localCreatedAt: entry.createdAt,
          });
          // Still apply — field appraiser's data takes precedence
        }
      }

      const { error } = await supabase
        .from('appraisals')
        .update(entry.payload)
        .eq('id', entry.appraisalId);

      if (error) throw error;

      await removeSyncEntry(entry.queueId!);
      flushed++;
    } catch (err) {
      console.error(`[syncQueue] Failed to flush entry ${entry.queueId}:`, err);
      failed++;
      // Leave in queue for next attempt — don't remove
    }
  }

  onProgress?.(failed > 0 ? 'error' : 'done');
  return { flushed, failed, conflicts };
}

/**
 * Enqueue an appraisal save for offline sync.
 * If we're online, this is a no-op (caller should save directly).
 */
export async function enqueueOfflineSave(
  appraisalId: string,
  userId: string,
  payload: any,
) {
  await enqueueSyncWrite(appraisalId, userId, payload);
}

/**
 * Flush queued photo uploads to Supabase Storage.
 *
 * Each entry is uploaded, a signed URL is created, and the entry is removed
 * from IndexedDB on success. Failed entries remain for the next attempt.
 *
 * Returns the URL map so callers can update optimistic blob URLs in state
 * if needed (keyed by fileName).
 */
export async function flushPendingPhotos(
  userId: string,
): Promise<{ flushed: number; failed: number }> {
  const entries = await getPendingPhotos(userId);
  if (entries.length === 0) return { flushed: 0, failed: 0 };

  let flushed = 0;
  let failed = 0;

  for (const entry of entries) {
    try {
      const blob = new Blob([entry.blobData], { type: entry.contentType });
      const folderPath = getAppraisalStoragePath(entry.userId, entry.appraisalId, entry.teamId, 'inspection');
      const filePath = `${folderPath}/${entry.fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('appraisals')
        .upload(filePath, blob, {
          contentType: entry.contentType,
          cacheControl: '31536000',
        });

      if (uploadError) throw uploadError;

      await removePendingPhoto(entry.id!);
      flushed++;
    } catch (err) {
      console.error(`[syncQueue] Failed to upload photo ${entry.fileName}:`, err);
      failed++;
    }
  }

  return { flushed, failed };
}
